import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../../supabase";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { CPInput } from "../CPInput";
import {
  activatePayrollType,
  createPayrollType,
  deactivatePayrollType,
  isReservedPayrollTypeKey,
  listPayrollTypes,
  setDefaultPayrollType,
  updatePayrollType,
} from "../../helpers/catalogs/payrollTypeCatalog";
import { resolvePayrollTypeDisplayLabel } from "../../helpers/payrollHelpers";
import { usePermissions } from "../../hooks/usePermissions";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { DeskEmpty, DeskPill, DeskTable } from "../desktop/DeskTable";
import { AdmMoveButtons } from "./desktop/AdmKit";

function payrollTypeListLabel(item) {
  return resolvePayrollTypeDisplayLabel(item.type_key, item.display_label);
}

export function AdminPayrollTypesScreen({ onBack }) {
  const { actor } = usePermissions();
  const isDesktop = useIsDesktopViewport();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [editingLabelValue, setEditingLabelValue] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const loadItems = useCallback(async () => {
    if (!actor) {
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const rows = await listPayrollTypes(supabase, {
        activeOnly: false,
        actor,
      });
      setItems(rows);
    } catch (err) {
      console.error("LOAD PAYROLL TYPES ERROR:", err);
      setError("Unable to load payroll types.");
    } finally {
      setLoading(false);
    }
  }, [actor]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleAdd = async () => {
    setError("");
    try {
      const nextOrder =
        items.reduce(
          (max, item) => Math.max(max, Number(item.display_order || 0)),
          0,
        ) + 1;

      await createPayrollType(
        {
          type_key: newKey,
          display_label: newLabel || newKey,
          display_order: nextOrder,
        },
        supabase,
        actor,
      );
      setNewKey("");
      setNewLabel("");
      await loadItems();
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("ADD PAYROLL TYPE ERROR:", err);
      setError(err.message || "Failed to add payroll type.");
    }
  };

  const handleToggleActive = async (item) => {
    setError("");
    try {
      if (item.is_active) {
        await deactivatePayrollType(item.payroll_type_id, supabase, actor);
      } else {
        await activatePayrollType(item.payroll_type_id, supabase, actor);
      }
      await loadItems();
    } catch (err) {
      console.error("TOGGLE PAYROLL TYPE ERROR:", err);
      setError(err.message || "Failed to update payroll type.");
    }
  };

  const handleSetDefault = async (item) => {
    setError("");
    try {
      await setDefaultPayrollType(item.payroll_type_id, supabase, actor);
      await loadItems();
    } catch (err) {
      console.error("DEFAULT PAYROLL TYPE ERROR:", err);
      setError(err.message || "Failed to set default payroll type.");
    }
  };

  const handleSaveLabel = async (item) => {
    setError("");
    try {
      await updatePayrollType(
        item.payroll_type_id,
        { display_label: editingLabelValue },
        supabase,
        actor,
      );
      setEditingLabelId(null);
      setEditingLabelValue("");
      await loadItems();
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("UPDATE PAYROLL TYPE LABEL ERROR:", err);
      setError(err.message || "Failed to update display label.");
    }
  };

  const handleMove = async (item, direction) => {
    setError("");
    const sorted = [...items].sort(
      (a, b) => Number(a.display_order) - Number(b.display_order),
    );
    const index = sorted.findIndex(
      (row) => row.payroll_type_id === item.payroll_type_id,
    );
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= sorted.length) {
      return;
    }

    const other = sorted[swapIndex];

    try {
      await Promise.all([
        updatePayrollType(
          item.payroll_type_id,
          { display_order: other.display_order },
          supabase,
          actor,
        ),
        updatePayrollType(
          other.payroll_type_id,
          { display_order: item.display_order },
          supabase,
          actor,
        ),
      ]);
      await loadItems();
    } catch (err) {
      console.error("REORDER PAYROLL TYPE ERROR:", err);
      setError(err.message || "Failed to reorder payroll types.");
    }
  };

  if (isDesktop) {
    const sorted = [...items].sort(
      (a, b) => Number(a.display_order) - Number(b.display_order),
    );
    return (
      <CrewPilotModuleShell
        title="Payroll Types"
        subtitle="Organization payroll type catalog — system keys are immutable"
        sectionLabel="Payroll"
        onBack={onBack}
        layout={PAGE_LAYOUT.COMMAND}
      >
        <div className="adm-main-side">
          {loading ? (
            <p className="adm-note">Loading…</p>
          ) : items.length === 0 ? (
            <DeskEmpty title="No payroll types found." />
          ) : (
            <DeskTable
              caption="Payroll types"
              rows={sorted}
              rowKey={(item) => item.payroll_type_id}
              rowClassName={(item) => (item.is_active ? "" : "is-muted")}
              columns={[
                {
                  key: "order",
                  label: "Order",
                  width: "104px",
                  render: (item) => (
                    <AdmMoveButtons
                      name={payrollTypeListLabel(item)}
                      onUp={() => handleMove(item, -1)}
                      onDown={() => handleMove(item, 1)}
                    />
                  ),
                },
                {
                  key: "label",
                  label: "Payroll type",
                  render: (item) =>
                    editingLabelId === item.payroll_type_id ? (
                      <div className="adm-edit">
                        <CPInput
                          className="cp-input-group--compact"
                          value={editingLabelValue}
                          onChange={setEditingLabelValue}
                          aria-label={`Display label for ${item.type_key}`}
                        />
                      </div>
                    ) : (
                      <>
                        <span className="dt-strong">{payrollTypeListLabel(item)}</span>
                        <span className="dt-sub adm-mono">{item.type_key}</span>
                        <span className="adm-rowact" style={{ justifyContent: "flex-start", flexWrap: "wrap", marginTop: "6px" }}>
                          <DeskPill tone={item.is_active ? "green" : "muted"}>
                            {item.is_active ? "Active" : "Inactive"}
                          </DeskPill>
                          {item.is_default ? <DeskPill tone="gold">Default</DeskPill> : null}
                          {item.is_system || isReservedPayrollTypeKey(item.type_key) ? (
                            <DeskPill tone="blue">System key</DeskPill>
                          ) : null}
                        </span>
                      </>
                    ),
                },
                {
                  key: "actions",
                  label: "Actions",
                  align: "right",
                  width: "330px",
                  render: (item) => (
                    <span className="adm-rowact">
                      {editingLabelId === item.payroll_type_id ? (
                        <>
                          <button
                            type="button"
                            className="kpi-back-btn adm-sm adm-primary"
                            onClick={() => handleSaveLabel(item)}
                          >
                            Save label
                          </button>
                          <button
                            type="button"
                            className="kpi-back-btn adm-sm"
                            onClick={() => {
                              setEditingLabelId(null);
                              setEditingLabelValue("");
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="kpi-back-btn adm-sm"
                            onClick={() => {
                              setEditingLabelId(item.payroll_type_id);
                              setEditingLabelValue(
                                item.display_label || payrollTypeListLabel(item),
                              );
                            }}
                          >
                            Edit label
                          </button>
                          {!item.is_default && item.is_active && (
                            <button
                              type="button"
                              className="kpi-back-btn adm-sm"
                              onClick={() => handleSetDefault(item)}
                            >
                              Set default
                            </button>
                          )}
                          {!item.is_system && (
                            <button
                              type="button"
                              className={`kpi-back-btn adm-sm${item.is_active ? " adm-soft" : ""}`}
                              onClick={() => handleToggleActive(item)}
                            >
                              {item.is_active ? "Deactivate" : "Activate"}
                            </button>
                          )}
                        </>
                      )}
                    </span>
                  ),
                },
              ]}
            />
          )}

          <div className="rb-card">
            <div className="section-heading">ADD PAYROLL TYPE</div>
            <div className="adm-fields adm-fields--1">
              <CPInput
                label="Type key (stored on rates/sessions)"
                value={newKey}
                onChange={setNewKey}
              />
              <CPInput
                label="Display label"
                value={newLabel}
                onChange={setNewLabel}
              />
            </div>
            <div className="adm-actions adm-actions--plain">
              <button type="button" className="kpi-back-btn adm-primary" onClick={handleAdd}>
                Add payroll type
              </button>
            </div>
            {error && (
              <p className="admin-placeholder-note" style={{ marginTop: "12px" }}>
                {error}
              </p>
            )}
            {saved && (
              <p className="admin-save-confirmation" role="status" style={{ marginTop: "12px" }}>
                Payroll type catalog updated.
              </p>
            )}
          </div>
        </div>
      </CrewPilotModuleShell>
    );
  }

  return (
    <CrewPilotModuleShell
      title="Payroll Types"
      subtitle="Organization payroll type catalog — system keys are immutable"
      sectionLabel="Payroll"
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
    >
      <div className="rb-card">
        <div className="section-heading">ADD PAYROLL TYPE</div>

        <CPInput
          label="Type key (stored on rates/sessions)"
          value={newKey}
          onChange={setNewKey}
        />

        <CPInput
          label="Display label"
          value={newLabel}
          onChange={setNewLabel}
        />

        <button type="button" className="kpi-back-btn" onClick={handleAdd}>
          Add payroll type
        </button>

        {error && (
          <p className="admin-placeholder-note" style={{ marginTop: "12px" }}>
            {error}
          </p>
        )}

        {saved && (
          <p className="admin-save-confirmation" role="status">
            Payroll type catalog updated.
          </p>
        )}
      </div>

      <div className="rb-card">
        <div className="section-heading">PAYROLL TYPES</div>

        {loading ? (
          <p className="admin-placeholder-note">Loading…</p>
        ) : items.length === 0 ? (
          <p className="admin-placeholder-note">No payroll types found.</p>
        ) : (
          items.map((item) => (
            <div
              key={item.payroll_type_id}
              className="admin-toggle-row"
              style={{
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <div style={{ flex: "1 1 220px" }}>
                {editingLabelId === item.payroll_type_id ? (
                  <CPInput
                    className="cp-input-group--compact"
                    value={editingLabelValue}
                    onChange={setEditingLabelValue}
                    aria-label={`Display label for ${item.type_key}`}
                  />
                ) : (
                  <>
                    <strong className="admin-data-primary">
                      {payrollTypeListLabel(item)}
                    </strong>
                    <span className="admin-data-secondary">
                      {" "}
                      ({item.type_key})
                    </span>
                  </>
                )}
                {item.is_default ? (
                  <span className="admin-data-secondary"> — default</span>
                ) : null}
                {item.is_system || isReservedPayrollTypeKey(item.type_key) ? (
                  <span className="admin-data-secondary"> — system key</span>
                ) : null}
                {!item.is_active ? (
                  <span className="admin-data-secondary"> — inactive</span>
                ) : null}
              </div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {editingLabelId === item.payroll_type_id ? (
                  <>
                    <button
                      type="button"
                      className="kpi-back-btn"
                      onClick={() => handleSaveLabel(item)}
                    >
                      Save label
                    </button>
                    <button
                      type="button"
                      className="kpi-back-btn"
                      onClick={() => {
                        setEditingLabelId(null);
                        setEditingLabelValue("");
                      }}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="kpi-back-btn"
                    onClick={() => {
                      setEditingLabelId(item.payroll_type_id);
                      setEditingLabelValue(
                        item.display_label || payrollTypeListLabel(item),
                      );
                    }}
                  >
                    Edit label
                  </button>
                )}
                <button
                  type="button"
                  className="kpi-back-btn"
                  onClick={() => handleMove(item, -1)}
                >
                  Up
                </button>
                <button
                  type="button"
                  className="kpi-back-btn"
                  onClick={() => handleMove(item, 1)}
                >
                  Down
                </button>
                {!item.is_default && item.is_active && (
                  <button
                    type="button"
                    className="kpi-back-btn"
                    onClick={() => handleSetDefault(item)}
                  >
                    Set default
                  </button>
                )}
                {!item.is_system && (
                  <button
                    type="button"
                    className="kpi-back-btn"
                    onClick={() => handleToggleActive(item)}
                  >
                    {item.is_active ? "Deactivate" : "Activate"}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </CrewPilotModuleShell>
  );
}
